'use client'

import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useResponsive, useTranslations, useQueryState } from '../hooks'
import Icon from '../icon'

type Props = {
  totalSize: number
  pageSize?: number
  defaultPage?: number
  pageChanged?: (page: number) => void
  containerClassName?: string
  scrollToTop?: boolean
  extState?: boolean
  extCurrentPage?: number
  queryState?: boolean
}

export const Pagination = ({
  totalSize,
  pageSize: pagesize,
  defaultPage,
  pageChanged,
  containerClassName,
  scrollToTop = false,
  extState,
  extCurrentPage,
  queryState: _queryState = true,
}: Props) => {
  const { breakpoints } = useResponsive()
  const { translate } = useTranslations()
  const {
    setQueryState,
    queryState: { page: queryPageNumber },
  } = useQueryState()

  const [currentPage, setcurrentPage] = useState<number>(defaultPage ?? 1)

  const totalPages = Math.ceil(totalSize / (pagesize ?? 10))

  useEffect(() => {
    if (totalPages === 1 && currentPage > 1) {
      setcurrentPage(1)
      if (pageChanged) {
        pageChanged(1)
      }
    }
  })

  useEffect(() => {
    if (_queryState) {
      setcurrentPage(queryPageNumber)
      if (pageChanged) {
        pageChanged(queryPageNumber)
      }
    }
  }, [queryPageNumber, _queryState])

  if (totalPages <= 1) {
    return null
  }

  const pageList: number[] = []
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
    pageList.push(pageNumber)
  }

  const changePage = (page: number, event: any) => {
    event.preventDefault()
    setcurrentPage(page)

    if (pageChanged) {
      pageChanged(page)
    }

    if (_queryState) {
      setQueryState({
        page: page.toString(),
      })
    }

    if (scrollToTop) {
      window.scrollTo(0, 0)
    }
  }

  const createHellipButton = (pageNumber: number) => {
    return (
      <div
        key={pageNumber}
        className="product-list-pagination c-button-group__btn d-flex align-items-center u-disabled">
        &hellip;
      </div>
    )
  }

  const page = extState && extCurrentPage ? extCurrentPage : currentPage
  const iconSize = breakpoints.min.MD ? 'i-32' : 'i-20'

  return (
    <div className={containerClassName}>
      <div className="c-button-group c-button-group--pagination u-mb-16">
        <button
          aria-label={translate('GoTo_FirstPage')}
          className="c-button-group__btn d-md-none"
          disabled={page <= 1}
          onClick={event => changePage(1, event)}>
          <Icon name="skip-left" className={classNames(iconSize, { 'u-disabled': page <= 1 })} />
        </button>
        <button
          aria-label={translate('GoTo_PreviousPage')}
          className="c-button-group__btn"
          disabled={page <= 1}
          onClick={event => changePage(page - 1, event)}>
          <Icon
            name="arrow-left-small"
            className={classNames(iconSize, { 'u-disabled': page <= 1 })}
          />
        </button>
        {pageList.map(pageNumber => {
          if (totalPages > 10) {
            if (page < 5) {
              if (pageNumber === 5) {
                return createHellipButton(pageNumber)
              } else if (pageNumber > 5 && pageNumber < totalPages) {
                return null
              }
            } else if (page > totalPages - 5) {
              if (pageNumber === 2) {
                return createHellipButton(pageNumber)
              } else if (pageNumber > 2 && pageNumber < totalPages - 4) {
                return null
              }
            } else {
              if (pageNumber === 2 || pageNumber === totalPages - 1) {
                return createHellipButton(pageNumber)
              } else if (
                pageNumber > 2 &&
                pageNumber < totalPages - 1 &&
                (pageNumber < page - 1 || pageNumber > page + 1)
              ) {
                return null
              }
            }
          }

          return (
            <button
              key={pageNumber}
              className={classNames('c-button-group__btn', 'd-none', 'd-md-flex', {
                'c-button-group__btn--active': pageNumber === page,
              })}
              onClick={event => changePage(pageNumber, event)}>
              {pageNumber}
            </button>
          )
        })}
        <button
          aria-label={translate('GoTo_NextPage')}
          className="c-button-group__btn"
          disabled={page >= totalPages}
          onClick={event => changePage(page + 1, event)}>
          <Icon
            name="arrow-right-small"
            className={classNames(iconSize, { 'u-disabled': page >= totalPages })}
          />
        </button>
        <button
          aria-label={translate('GoTo_LastPage')}
          className="c-button-group__btn d-md-none"
          disabled={page >= totalPages}
          onClick={event => changePage(totalPages, event)}>
          <Icon
            name="skip-right"
            className={classNames(iconSize, { 'u-disabled': page >= totalPages })}
          />
        </button>
      </div>
      <div className="u-text-center col-12 d-md-none u-mb-16">
        {translate('PaginationText', {
          page: page,
          totalPages,
        })}
      </div>
    </div>
  )
}

export default Pagination
